/*
 * "Notify me when back in stock".
 *
 * A sold-out size can still be chosen on a product page (snippets/item-sizes.liquid);
 * ADD TO CART is then replaced by NOTIFY ME, which opens the bottom panel with the
 * sign-up form (snippets/form-back-in-stock.liquid) instead of the newsletter.
 *
 * The address goes straight from the browser to Klaviyo — the same Klaviyo that sends
 * the newsletter — with its public company id. Klaviyo watches Shopify's stock and
 * emails the person when that exact size is back.
 *
 * Two calls, both needed: one records the request against the size, the other subscribes
 * the person. Klaviyo counts a back-in-stock alert as marketing (they refused it
 * transactional status), so it only reaches a subscribed profile — without the second
 * call the alert is recorded and then silently skipped. Asking for the alert is the
 * consent, and the panel says so (snippets/form-back-in-stock.liquid).
 */
import modalBottom from '../modules/modal-bottom';

const panel = document.querySelector('.m-stock') as HTMLElement | null;
const newsletter = document.querySelector('.m-newsletter') as HTMLElement | null;

const form = panel?.querySelector('.m-stock__form') as HTMLFormElement | null;
const email = panel?.querySelector('.m-stock__email') as HTMLInputElement | null;
const submit = panel?.querySelector('.m-stock__submit') as HTMLButtonElement | null;
const errorBox = panel?.querySelector('.m-stock__error') as HTMLElement | null;
const successBox = panel?.querySelector('.m-stock__success') as HTMLElement | null;
const productName = panel?.querySelector('.m-stock__product') as HTMLElement | null;
const sizeName = panel?.querySelector('.m-stock__size') as HTMLElement | null;
const closeButton = panel?.querySelector('.m-stock__close') as HTMLButtonElement | null;

let variantId = '';

const reset = () => {
  if (errorBox) errorBox.textContent = '';
  if (successBox) {
    successBox.hidden = true;
    successBox.textContent = '';
  }
  if (form) form.hidden = false;
  if (submit) {
    submit.disabled = false;
    submit.classList.remove('is-sending');
  }
};

/* Swap the newsletter for the sign-up and open the panel. */
export const openBackInStock = (variant: string, size: string, product: string) => {
  if (!panel) return;

  variantId = variant;
  if (productName) productName.textContent = product;
  if (sizeName) sizeName.textContent = size;

  reset();
  // open first: the panel always opens on the newsletter, then we swap it for this
  modalBottom.showModal();
  if (newsletter) newsletter.hidden = true;
  panel.hidden = false;
  email?.focus();
};

const close = () => {
  modalBottom.hideModal();
  if (panel) panel.hidden = true;
  if (newsletter) newsletter.hidden = false;
};

closeButton?.addEventListener('click', close);

form?.addEventListener('submit', async (event) => {
  event.preventDefault();
  if (!email?.value || !variantId) return;

  const company = panel?.dataset.klaviyoCompany;
  if (!company) return;

  if (submit) {
    submit.disabled = true;
    // the label itself is in the markup, so it can differ by screen size
    submit.classList.add('is-sending');
  }
  if (errorBox) errorBox.textContent = '';

  const klaviyo = (path: string, body: unknown) =>
    fetch(`https://a.klaviyo.com/client/${path}/?company_id=${company}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: '2026-07-15',
      },
      body: JSON.stringify(body),
    });

  try {
    const list = panel?.dataset.klaviyoList;

    const [subscription, request] = await Promise.all([
      klaviyo('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email.value,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      }),
      klaviyo('back-in-stock-subscriptions', {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email: email.value } } },
          },
          relationships: {
            variant: {
              // how Klaviyo names a Shopify variant in its catalogue
              data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
            },
          },
        },
      }),
    ]);

    const response = request;
    if (!subscription.ok) throw new Error(`subscription ${subscription.status}`);
    if (!response.ok) throw new Error(String(response.status));

    if (form) form.hidden = true;
    if (successBox) {
      successBox.textContent = 'Thank you. We will email you as soon as this size is back.';
      successBox.hidden = false;
    }
  } catch {
    if (errorBox) {
      errorBox.textContent = 'Sorry, that did not go through. Please try again in a moment.';
    }
    if (submit) {
      submit.disabled = false;
      submit.classList.remove('is-sending');
    }
  }
});

export default { openBackInStock };
